import React from 'react';
import { Award, Users, ShieldCheck, TrendingUp, Heart, Wrench } from 'lucide-react';

const About = () => {
  const milestones = [
    {
      year: '1996',
      title: 'Foundation',
      description: 'Started as a small shop in Nehru Nagar with a vision to provide honest TV repair services'
    },
    {
      year: '2000s',
      title: 'LCD Era',
      description: 'Adapted to flat-screen technology, becoming early experts in LCD TV repairs'
    },
    {
      year: '2010s',
      title: 'LED & Smart TV',
      description: 'Mastered LED and Smart TV technology, expanded service across Kanpur'
    },
    {
      year: '2020s',
      title: 'Modern Excellence',
      description: '10,000+ repairs completed, OLED expertise, and Kanpur\'s most trusted name'
    }
  ];

  const values = [
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: 'Trust & Transparency',
      description: 'No hidden charges, honest diagnosis, fair pricing always'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Expert Excellence',
      description: 'Certified technicians with 30+ years of combined experience'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. 95% customer satisfaction rate'
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: 'Continuous Innovation',
      description: 'Always learning, adapting to latest TV technology'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About Garg Electronics
            </h1>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto">
              Kanpur's Most Trusted LED/LCD/Smart TV Repair Service Since 1996
            </p>
          </div>
        </div>
      </section>

      {/* Shop Image Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/jbq6lskk_1000036486.jpg"
                alt="Garg Electronics Shop - Expert Technician at Work Repairing TV"
                loading="lazy"
                className="rounded-xl shadow-2xl w-full"
              />
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Legacy: 30+ Years of Excellence
              </h2>
              <p className="text-lg text-gray-700 mb-4">
                Located at <strong>107/208 Nehru Nagar, Kanpur</strong>, Garg Electronics has been serving the community since 1996. What started as a modest electronics repair shop has grown into Kanpur's most trusted specialized TV repair service.
              </p>
              <p className="text-lg text-gray-700 mb-4">
                Founded by <strong>Mr. Puneet Garg</strong>, our journey began during the transformation from traditional CRT televisions to modern flat-screen technology. While others struggled to adapt, we invested in training, tools, and expertise to become masters of LCD, LED, and Smart TV technology.
              </p>
              <div className="grid grid-cols-2 gap-4 mt-8">
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <div className="text-3xl font-bold text-gray-900 mb-2">10,000+</div>
                  <div className="text-gray-700 font-medium">TVs Repaired</div>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                  <div className="text-3xl font-bold text-gray-900 mb-2">30+</div>
                  <div className="text-gray-700 font-medium">Years Experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our Journey Through Television Evolution
          </h2>
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-gray-200"></div>
            
            {/* Timeline Items */}
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`relative grid grid-cols-1 md:grid-cols-2 gap-8 ${index % 2 === 0 ? '' : 'md:flex-row-reverse'}`}>
                  {/* Left Side (or Right on odd indexes) */}
                  <div className={`${index % 2 === 0 ? 'md:text-right' : 'md:order-2'}`}>
                    <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200 inline-block">
                      <div className="text-4xl font-bold text-gray-900 mb-2">{milestone.year}</div>
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 items-center justify-center">
                    <div className="w-6 h-6 bg-gray-900 rounded-full border-4 border-white shadow-lg"></div>
                  </div>
                  
                  {/* Right Side (or Left on odd indexes) */}
                  <div className={`${index % 2 === 0 ? 'md:order-2' : ''}`}></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            What Sets Us Apart
          </h2>
          <p className="text-lg text-gray-600 text-center mb-12 max-w-3xl mx-auto">
            Unlike general electronics repair shops, we specialize exclusively in modern flat-screen television repairs
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-md">
              <Wrench className="w-12 h-12 text-gray-900 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Specialized Expertise</h3>
              <p className="text-gray-700 mb-4">
                We don't repair mobile phones or washing machines. We focus 100% on LED, LCD, OLED, and Smart TVs. This singular focus has made us experts in every aspect of modern television technology.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gray-900 mr-2">✓</span>
                  Factory-trained technicians
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-2">✓</span>
                  Board-level repair capabilities
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-2">✓</span>
                  Smart TV software expertise
                </li>
              </ul>
            </div>

            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-md">
              <Users className="w-12 h-12 text-gray-900 mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Quality Parts Sourcing</h3>
              <p className="text-gray-700 mb-4">
                Over 30 years, we've built strong relationships with trusted suppliers in Delhi, Mumbai, and Chennai. We source high-quality A-grade parts, not cheap duplicates.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <span className="text-gray-900 mr-2">✓</span>
                  Direct wholesale market sourcing
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-2">✓</span>
                  40-60% cost savings vs authorized centers
                </li>
                <li className="flex items-start">
                  <span className="text-gray-900 mr-2">✓</span>
                  120-day warranty on all parts
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 shadow-md hover:shadow-lg transition-shadow text-center">
                <div className="text-gray-900 mb-4 flex justify-center">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            Our Expert Team
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/l7qtmycz_1000035874.png"
                alt="Expert Technician"
                className="w-full h-80 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Certified Technicians</h3>
                <p className="text-gray-600">
                  Our team comprises factory-trained professionals who undergo rigorous training from major TV manufacturers like Samsung, LG, and Sony.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-xl overflow-hidden shadow-lg">
              <img
                src="https://customer-assets.emergentagent.com/job_tv-repair-pro-2/artifacts/pzxlqaco_1000035856.png"
                alt="Professional Service"
                className="w-full h-80 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Background Verified Staff</h3>
                <p className="text-gray-600">
                  Every technician is background verified for your safety and security. We prioritize your trust and peace of mind.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Our Commitment to Kanpur
          </h2>
          <p className="text-xl mb-6">
            We are deeply rooted in Kanpur. From Nehru Nagar to Govind Nagar, from Kalyanpur to Swaroop Nagar, we serve every corner of the city we call home.
          </p>
          <p className="text-lg">
            Over 95% of our business comes from repeat customers and referrals - a testament to the trust Kanpur families place in us. Your modern flat-screen television is an investment in your family's entertainment. Trust it with Kanpur's most experienced experts.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
